/**
 * +world-resources: what each world object is and drops (docs/design/world-resources.md §2).
 * Amounts are tuned so a run that breaks what lies on its route pays ~15% of the run's
 * resources from the world and a full explore ~25% (see world.test.ts "income share").
 */
import type { WorldLootEntry, WorldObjectClass, WorldObjectKind } from '@keystone/sim';

export interface WorldObjectKindDef {
  class: WorldObjectClass;
  /** Hits to break (caches open by standing at them instead). */
  hp: number;
  radius: number;
  loot: WorldLootEntry[];
}

/** Eye fragments only drop on keys at or above this level (≈ Champion territory, crafting.md §1). */
export const WORLD_EYE_MIN_KEY = 9;

export const WORLD_OBJECT_KINDS: Readonly<Record<WorldObjectKind, WorldObjectKindDef>> = {
  // Breakables: one hit (crates and cocoons take two). Mostly gold for the burst, a sliver of metal.
  urn: { class: 'breakable', hp: 1, radius: 0.5, loot: [{ resource: 'gold', chance: 1, min: 2, max: 5 }, { resource: 'fangsteel', chance: 0.06, min: 1, max: 1 }] },
  crate: { class: 'breakable', hp: 2, radius: 0.6, loot: [{ resource: 'gold', chance: 0.6, min: 2, max: 4 }, { resource: 'fangsteel', chance: 0.12, min: 1, max: 1 }] },
  bone_pile: { class: 'breakable', hp: 1, radius: 0.5, loot: [{ resource: 'gold', chance: 0.6, min: 1, max: 3 }, { resource: 'fangsteel', chance: 0.05, min: 1, max: 1 }] },
  egg_clutch: {
    class: 'breakable',
    hp: 1,
    radius: 0.6,
    loot: [
      { resource: 'gold', chance: 0.4, min: 1, max: 2 },
      { resource: 'moltskin', chance: 0.006, min: 1, max: 1 }, // +econ-pacing: was 3% (world Moltskin was 53–66% of income, mostly unspent)
    ],
  },
  cocoon: {
    class: 'breakable',
    hp: 2,
    radius: 0.55,
    loot: [
      { resource: 'gold', chance: 0.5, min: 2, max: 3 },
      { resource: 'fangsteel', chance: 0.06, min: 1, max: 1 },
      { resource: 'moltskin', chance: 0.004, min: 1, max: 1 }, // +econ-pacing: was 2%
    ],
  },
  offering_bowl: { class: 'breakable', hp: 1, radius: 0.45, loot: [{ resource: 'gold', chance: 1, min: 3, max: 7 }, // +econ-pacing: was 4–8
     { resource: 'scale', chance: 0.03, min: 1, max: 1 }] },
  // +field: field treasure chests roll their own loot when they spawn (sim field/loot.ts); this
  // entry only sizes and classes them.
  treasure_chest: { class: 'cache', hp: 1, radius: 0.9, loot: [] },
  // Hidden caches: the odds shown on the cache tooltip (crafting.md §2.1).
  hidden_cache: {
    class: 'cache',
    hp: 1,
    radius: 0.8,
    loot: [
      { resource: 'gold', chance: 1, min: 8, max: 16 }, // +econ-pacing: was 15–30 (explore gold share 30%, band 15–25%)
      { resource: 'fangsteel', chance: 0.6, min: 1, max: 1 },
      { resource: 'venom', chance: 0.2, min: 1, max: 1 },
      { resource: 'moltskin', chance: 0.03, min: 1, max: 1 }, // +econ-pacing: was 10%
      { resource: 'eyeFragment', chance: 0.05, min: 1, max: 1, minLevel: WORLD_EYE_MIN_KEY },
      { resource: 'scaleUp', chance: 0.01, min: 3, max: 3 },
    ],
  },
  // Rare reagent nodes: three hits.
  venom_gland: {
    class: 'node',
    hp: 3,
    radius: 0.7,
    loot: [
      { resource: 'venom', chance: 0.2, min: 1, max: 1 },
      { resource: 'fangsteel', chance: 0.25, min: 1, max: 1 },
      { resource: 'gold', chance: 1, min: 3, max: 6 },
    ],
  },
  eye_shard: {
    class: 'node',
    hp: 3,
    radius: 0.7,
    loot: [
      { resource: 'fangsteel', chance: 1, min: 1, max: 1 },
      { resource: 'eyeFragment', chance: 0.08, min: 1, max: 1, minLevel: WORLD_EYE_MIN_KEY },
      { resource: 'gold', chance: 1, min: 3, max: 6 },
    ],
  },
};

/** Fangsteel-equivalent value weights used for the income-share check (docs §4). */
export const WORLD_VALUE: Readonly<Record<string, number>> = { fangsteel: 1, scale: 2, scaleUp: 3, venom: 3, moltskin: 4, eyeFragment: 10, gold: 0 };
